export interface PerformanceMetrics {
  loadTime?: string;
  bounceRate?: string;
  mobileScore?: string;
  recoveryTime?: string;
  userConfidence?: string;
  processSteps?: string;
}

export interface ProjectPerformanceData {
  month: string;
  sessions2024?: number;
  visitors2024?: number;
  sessions2023?: number;
  visitors2023?: number;
  before?: number;
  after?: number;
  automationLevel?: number;
  userConfidence?: number;
  Manual?: number;
  Automated?: number;
}

export interface Project {
  id: number;
  title: string;
  clientIndustry: string;
  year: string;
  type: 'Web Development' | 'Optimization' | 'Automation';
  categories: string[];
  challenge: string;
  solution: string;
  results: string;
  'load time improvement'?: string;
  'process reduction'?: string;
  'error rate'?: string;
  'data accuracy'?: string;
  'decision speed'?: string;
  'build time'?: string;
  'traffic growth'?: string;
  'user adoption'?: string;
  techStack: string[];
  performanceMetrics?: {
    beforeOptimization: PerformanceMetrics;
    afterOptimization: PerformanceMetrics;
  };
  performanceData: ProjectPerformanceData[];
  keyAchievements?: string[];
  [key: string]: any;
}

export const portfolioData: Project[] = [
  {
    id: 1,
    categories: ['web', 'mobile', 'desktop'],
    title: "Shee & Fowl",
    clientIndustry: "E-commerce / Online Retail",
    year: "2023",
    type: "Web Development",
    challenge: "Client needed a fully designed and deployed e-commerce storefront from scratch, with only a domain name and a 5-day timeline.",
    solution: "Built a lightweight site using vanilla technologies and AWS infrastructure. Implemented CI/CD workflow for rapid iterations during the 5-day build.",
    results: "Completed full site build and deployment in under 5 days",
    'build time': "5 days",
    techStack: ['HTML', 'CSS', 'JavaScript', 'AWS S3', 'AWS CloudFront', 'CI/CD'],
    performanceData: [
      { month: 'Day 1', before: 0, after: 25 },
      { month: 'Day 2', before: 0, after: 45 },
      { month: 'Day 3', before: 0, after: 65 },
      { month: 'Day 4', before: 0, after: 85 },
      { month: 'Day 5', before: 0, after: 100 }
    ]
  },
  {
    id: 2,
    categories: ['web', 'mobile', 'desktop'],
    title: "Bloom Elixirs",
    clientIndustry: "Health & Wellness (Shopify)",
    year: "2023-2024",
    type: "Optimization",
    challenge: "E-commerce site suffering from severe performance issues, including slow load times and poor mobile optimization, leading to high bounce rates and lost sales. Analytics showed 69% of visitors were leaving before completing purchases.",
    solution: "Implemented comprehensive optimization strategy:\n- Migrated all product images to WebP format\n- Developed custom lazy-loading solution\n- Streamlined JavaScript bundle sizes\n- Implemented server-side caching\n- Created Docker-based development environment for rapid testing",
    results: "Achieved dramatic improvements in key metrics:\n- 83% reduction in page load times\n- 400% increase in monthly traffic\n- 30% boost in conversion rates",
    'load time improvement': "83%",
    'process reduction': "-20%",
    'traffic growth': "+400%",
    techStack: ['Shopify', 'Node.js', 'Python', 'Docker', 'WebP', 'Redis Cache', 'CI/CD', 'Google Analytics'],
    performanceMetrics: {
      beforeOptimization: {
        loadTime: "6.9s",
        bounceRate: "69%",
        mobileScore: "45/100"
      },
      afterOptimization: {
        loadTime: "1.2s",
        bounceRate: "31%",
        mobileScore: "92/100"
      }
    },
    performanceData: [
      { month: 'May', sessions2024: 3862, visitors2024: 3610, sessions2023: 806, visitors2023: 697 },
      { month: 'Jun', sessions2024: 5410, visitors2024: 5045, sessions2023: 393, visitors2023: 344 },
      { month: 'Jul', sessions2024: 5659, visitors2024: 5392, sessions2023: 468, visitors2023: 382 },
      { month: 'Aug', sessions2024: 19540, visitors2024: 17572, sessions2023: 1171, visitors2023: 908 },
      { month: 'Sep', sessions2024: 1710, visitors2024: 1553, sessions2023: 889, visitors2023: 745 },
      { month: 'Oct', sessions2024: 3231, visitors2024: 2986, sessions2023: 870, visitors2023: 707 },
      { month: 'Nov', sessions2024: 20, visitors2024: 17, sessions2023: 86, visitors2023: 82 }
    ]
  },
  {
    id: 3,
    categories: ['cloud', 'web'],
    title: "Secure VPS Automation & Backup",
    clientIndustry: "Enterprise / Cloud Infrastructure",
    year: "2024",
    type: "Automation",
    challenge: "Client needed a simplified VPS management system for non-technical staff, focusing on automated backups and easy recovery. The main challenge was bridging the technical complexity gap, as 99% of work was browser-based and staff had limited technical expertise.",
    solution: "Implemented a comprehensive yet user-friendly cloud management solution:\n- Created IAM user access with simplified permissions\n- Developed automated snapshot system with email notifications\n- Established clear documentation with visual guides\n- Streamlined recovery procedures for non-technical users\n- Implemented Chrome-focused backup strategy",
    results: "Created a fully automated system with 99.9% uptime and simplified recovery process",
    'process reduction': "-70%",
    'data accuracy': "99.9%",
    'user adoption': "100%",
    techStack: ['AWS Lightsail', 'IAM', 'Shell Scripting', 'Chrome Sync', 'Automation', 'Technical Documentation'],
    performanceMetrics: {
      beforeOptimization: {
        recoveryTime: "4 hours",
        userConfidence: "20%",
        processSteps: "15"
      },
      afterOptimization: {
        recoveryTime: "15 min",
        userConfidence: "95%",
        processSteps: "3"
      }
    },
    performanceData: [
      { month: 'Week 1', automationLevel: 20, userConfidence: 20, Manual: 240, Automated: 60 },
      { month: 'Week 2', automationLevel: 45, userConfidence: 45, Manual: 240, Automated: 45 },
      { month: 'Week 3', automationLevel: 70, userConfidence: 65, Manual: 240, Automated: 30 },
      { month: 'Week 4', automationLevel: 85, userConfidence: 85, Manual: 240, Automated: 20 },
      { month: 'Week 5', automationLevel: 95, userConfidence: 95, Manual: 240, Automated: 15 }
    ],
    keyAchievements: [
      "Reduced system recovery time from 4 hours to 15 minutes",
      "Increased user confidence in system management from 20% to 95%",
      "Simplified 15-step recovery process to 3 steps",
      "Automated notification system for backup status",
      "Implemented secure IAM access management",
      "Achieved 99.9% data accuracy in automated operations"
    ]
  }
]; 